import { useLayoutEffect, useRef, useState } from "react";

export function useRetainedModalData<T>(data: T | null | undefined) {
  const [state, setState] = useState(() => ({
    input: data,
    retainedData: data ?? undefined,
    // Identifies input changes so an old close callback cannot clear newer data.
    revision: 0,
    // Modal.Data uses this as its content key. Reopening during a leave must also
    // remount the content, giving forms fresh state without waiting for cleanup.
    session: 0,
  }));
  const current = useRef({ data, revision: state.revision, completed: false });

  useLayoutEffect(() => {
    current.current = { data, revision: state.revision, completed: false };
  }, [data, state.revision]);

  // Sync during render so React retries this component before committing children.
  // An effect would briefly commit new data with the previous opening's content key.
  if (!Object.is(data, state.input)) {
    setState({
      input: data,
      retainedData: data ?? state.retainedData,
      revision: state.revision + 1,
      session: state.session + (state.input == null && data != null ? 1 : 0),
    });
  }

  function completeClose() {
    // Ignore completion if reopened, if this callback belongs to an older input,
    // or if cleanup already ran. The revision check also covers reopening and
    // closing again before an earlier close callback arrives.
    if (
      current.current.data != null ||
      current.current.revision !== state.revision ||
      current.current.completed
    ) {
      return false;
    }
    current.current.completed = true;
    setState((state) => ({ ...state, retainedData: undefined }));
    return true;
  }

  return {
    data: data ?? state.retainedData,
    show: data != null,
    session: state.session,
    completeClose,
  };
}
